// Schnellzugriff: mit Strg/Cmd + K öffnen, tippen, Enter. Damit ist jede Seite
// mit zwei Handgriffen erreichbar, statt sie in der Seitenleiste zu suchen —
// und jeder Kunde und jeder Beleg: „meyer" öffnet die Kundenakte, „RE-2026"
// die Rechnung.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSprache } from '../lib/i18n';
import type { Schluessel } from '../lib/i18n';
import Icon from './Icon';
import type { IconName } from './Icon';

export type Ziel = { pfad: string; schluessel: Schluessel; symbol: IconName; gesperrt?: boolean };

type Funde = {
  kunden: Array<{ id: string; name: string; kundennummer: string; ort: string }>;
  dokumente: Array<{ art: string; id: string; nummer: string; kundeName: string }>;
};
type Eintrag = { pfad: string; name: string; zusatz?: string; symbol: IconName; gesperrt?: boolean; gruppe?: string };

const dokumentPfad = (d: { art: string; id: string }) =>
  d.art === 'rechnung' ? `/rechnungen?id=${d.id}` : `/belege?art=${d.art}&id=${d.id}`;

export default function Schnellzugriff({ ziele, offen, schliessen }: {
  ziele: Ziel[];
  offen: boolean;
  schliessen: () => void;
}) {
  const { t } = useSprache();
  const navigate = useNavigate();
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const eingabeRef = useRef<HTMLInputElement>(null);

  const [funde, setFunde] = useState<Funde>({ kunden: [], dokumente: [] });

  // Kunden und Belege beim Server suchen — kurz gewartet, damit nicht jeder
  // Tastendruck eine Anfrage wird.
  useEffect(() => {
    const s = suche.trim();
    if (s.length < 2) { setFunde({ kunden: [], dokumente: [] }); return; }
    const steuerung = new AbortController();
    const id = setTimeout(() => {
      api<Funde>(`/customers/finden?q=${encodeURIComponent(s)}`, { signal: steuerung.signal })
        .then(setFunde).catch(() => { /* Suche ist Komfort — ohne Treffer geht es auch */ });
    }, 200);
    return () => { clearTimeout(id); steuerung.abort(); };
  }, [suche]);

  const treffer = useMemo<Eintrag[]>(() => {
    const s = suche.trim().toLowerCase();
    const seiten: Eintrag[] = ziele.map(z => ({ pfad: z.pfad, name: t(z.schluessel), symbol: z.symbol, gesperrt: z.gesperrt }))
      .filter(z => !s || z.name.toLowerCase().includes(s));
    const kunden: Eintrag[] = funde.kunden.map((k, i) => ({
      pfad: `/kunden/${k.id}`, name: k.name, symbol: 'kunden',
      zusatz: [k.kundennummer && `#${k.kundennummer}`, k.ort].filter(Boolean).join(' · '),
      gruppe: i === 0 ? t('nav.kunden') : undefined,
    }));
    const dokumente: Eintrag[] = funde.dokumente.map((d, i) => ({
      pfad: dokumentPfad(d), name: d.nummer, symbol: d.art === 'rechnung' ? 'rechnungen' : 'belege',
      zusatz: [t(`kunden.art.${d.art}` as Schluessel), d.kundeName].filter(Boolean).join(' · '),
      gruppe: i === 0 ? t('kunden.dokumente') : undefined,
    }));
    return [...seiten, ...kunden, ...dokumente];
  }, [ziele, suche, t, funde]);

  useEffect(() => {
    if (offen) {
      setSuche('');
      setMarkiert(0);
      // Kurz warten, bis das Feld im Baum ist.
      requestAnimationFrame(() => eingabeRef.current?.focus());
    }
  }, [offen]);

  useEffect(() => { setMarkiert(0); }, [suche]);

  if (!offen) return null;

  function springen(pfad: string) {
    schliessen();
    navigate(pfad);
  }

  function tasten(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { schliessen(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarkiert(i => Math.min(i + 1, treffer.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setMarkiert(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && treffer[markiert]) { e.preventDefault(); springen(treffer[markiert].pfad); }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={schliessen}
      role="dialog"
      aria-modal="true"
      aria-label={t('allg.schnellzugriff')}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={e => e.stopPropagation()}
      >
        <input
          ref={eingabeRef}
          value={suche}
          onChange={e => setSuche(e.target.value)}
          onKeyDown={tasten}
          placeholder={t('allg.schnellzugriffHinweis')}
          className="w-full border-b border-slate-200 bg-transparent px-4 py-3.5 text-slate-900 outline-none placeholder:text-slate-400 dark:border-slate-700 dark:text-slate-100"
        />

        {treffer.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">{t('allg.keineTreffer')}</p>
        ) : (
          <ul className="max-h-[50vh] overflow-y-auto py-1">
            {treffer.map((z, i) => (
              <li key={z.pfad}>
                {z.gruppe && (
                  <p className="border-t border-slate-100 px-4 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-800">{z.gruppe}</p>
                )}
                <button
                  onClick={() => springen(z.pfad)}
                  onMouseEnter={() => setMarkiert(i)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-start text-sm ${
                    i === markiert
                      ? 'bg-marke-50 text-marke-700 dark:bg-marke-950/60 dark:text-marke-300'
                      : 'text-slate-700 dark:text-slate-300'}`}
                >
                  <Icon name={z.symbol} groesse={18} />
                  <span className="flex-1 truncate">{z.name}</span>
                  {z.zusatz && <span className="truncate text-xs text-slate-500 dark:text-slate-400">{z.zusatz}</span>}
                  {z.gesperrt && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      {t('allg.tarif')}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
